import { Color3, Material, MaterialPluginBase, Vector3 } from 'babylonjs';
import type { AbstractEngine, Scene, SubMesh, UniformBuffer } from 'babylonjs';

/**
 * Material plugin that paints the detection pulse + region-reveal wave directly
 * on the model's own PBR materials (no overlay geometry). It is driven entirely by
 * uniforms set per-frame from `RegionFx`, so it never recompiles after attach:
 *   - a world-space ring radiating from the tap point (masks worker latency)
 *   - a wavefront reveal keyed on the per-vertex `ejDepth` attribute (BFS depth)
 *
 * Modes: 0 off · 1 ring · 2 ring+wave · 3 hold (solid region highlight).
 */
export class PlaneFinderFxPlugin extends MaterialPluginBase {
  mode = 0;
  pulseOrigin = new Vector3();
  pulseRadius = 0;
  pulseWidth = 0.05;
  waveProgress = 0;
  waveMaxDepth = 1;
  strength = 0;
  tint = new Color3(0.25, 0.85, 1.0);
  pulseTint = new Color3(0.5, 0.95, 1.0);

  // Foreground/back culling: discard fragments outside a slab around the detected
  // plane. cullFade (0→1) dither-dissolves the outside (eased in over the camera
  // fly); at 1 it hard-culls. cullNormal points toward the camera.
  cullEnabled = 0;
  cullOrigin = new Vector3();
  cullNormal = new Vector3();
  cullFront = 0;
  cullBack = 0;
  cullFade = 0;

  constructor(material: Material) {
    super(material, 'PlaneFinderFx', 200, { PLANEFINDERFX: true });
    this._enable(true);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  override prepareDefines(defines: any) {
    defines.PLANEFINDERFX = true;
  }

  override getClassName() {
    return 'PlaneFinderFxPlugin';
  }

  override getAttributes(attributes: string[]) {
    attributes.push('ejDepth');
  }

  override getUniforms() {
    return {
      ubo: [
        { name: 'ejMode', size: 1, type: 'float' },
        { name: 'ejPulseOrigin', size: 3, type: 'vec3' },
        { name: 'ejPulseRadius', size: 1, type: 'float' },
        { name: 'ejPulseWidth', size: 1, type: 'float' },
        { name: 'ejWaveProgress', size: 1, type: 'float' },
        { name: 'ejWaveMaxDepth', size: 1, type: 'float' },
        { name: 'ejStrength', size: 1, type: 'float' },
        { name: 'ejTint', size: 3, type: 'vec3' },
        { name: 'ejPulseTint', size: 3, type: 'vec3' },
        { name: 'ejCullEnabled', size: 1, type: 'float' },
        { name: 'ejCullOrigin', size: 3, type: 'vec3' },
        { name: 'ejCullNormal', size: 3, type: 'vec3' },
        { name: 'ejCullFront', size: 1, type: 'float' },
        { name: 'ejCullBack', size: 1, type: 'float' },
        { name: 'ejCullFade', size: 1, type: 'float' },
      ],
      // GLSL declarations for the fragment stage (Babylon packs them into the
      // Material UBO on WebGL2; without this string the uniforms are undeclared).
      fragment: `#ifdef PLANEFINDERFX
        uniform float ejMode;
        uniform vec3 ejPulseOrigin;
        uniform float ejPulseRadius;
        uniform float ejPulseWidth;
        uniform float ejWaveProgress;
        uniform float ejWaveMaxDepth;
        uniform float ejStrength;
        uniform vec3 ejTint;
        uniform vec3 ejPulseTint;
        uniform float ejCullEnabled;
        uniform vec3 ejCullOrigin;
        uniform vec3 ejCullNormal;
        uniform float ejCullFront;
        uniform float ejCullBack;
        uniform float ejCullFade;
      #endif`,
    };
  }

  override bindForSubMesh(uniformBuffer: UniformBuffer, _scene: Scene, _engine: AbstractEngine, _subMesh: SubMesh) {
    uniformBuffer.updateFloat('ejMode', this.mode);
    uniformBuffer.updateFloat3('ejPulseOrigin', this.pulseOrigin.x, this.pulseOrigin.y, this.pulseOrigin.z);
    uniformBuffer.updateFloat('ejPulseRadius', this.pulseRadius);
    uniformBuffer.updateFloat('ejPulseWidth', this.pulseWidth);
    uniformBuffer.updateFloat('ejWaveProgress', this.waveProgress);
    uniformBuffer.updateFloat('ejWaveMaxDepth', this.waveMaxDepth);
    uniformBuffer.updateFloat('ejStrength', this.strength);
    uniformBuffer.updateColor3('ejTint', this.tint);
    uniformBuffer.updateColor3('ejPulseTint', this.pulseTint);
    uniformBuffer.updateFloat('ejCullEnabled', this.cullEnabled);
    uniformBuffer.updateFloat3('ejCullOrigin', this.cullOrigin.x, this.cullOrigin.y, this.cullOrigin.z);
    uniformBuffer.updateFloat3('ejCullNormal', this.cullNormal.x, this.cullNormal.y, this.cullNormal.z);
    uniformBuffer.updateFloat('ejCullFront', this.cullFront);
    uniformBuffer.updateFloat('ejCullBack', this.cullBack);
    uniformBuffer.updateFloat('ejCullFade', this.cullFade);
  }

  override getCustomCode(shaderType: string): { [name: string]: string } | null {
    if (shaderType === 'vertex') {
      return {
        CUSTOM_VERTEX_DEFINITIONS: `
          attribute float ejDepth;
          varying float vEjDepth;
          varying vec3 vEjWorldPos;
        `,
        CUSTOM_VERTEX_MAIN_END: `
          vEjDepth = ejDepth;
          // Own world-position varying — do NOT rely on PBR's vPositionW, which
          // is absent in the unlit shader path (capture renders unlit).
          vEjWorldPos = (finalWorld * vec4(positionUpdated, 1.0)).xyz;
        `,
      };
    }
    if (shaderType === 'fragment') {
      return {
        CUSTOM_FRAGMENT_DEFINITIONS: `
          varying float vEjDepth;
          varying vec3 vEjWorldPos;
        `,
        CUSTOM_FRAGMENT_MAIN_END: `
          #ifdef PLANEFINDERFX
          // Cull: discard fragments outside the slab around the plane. cullFade
          // dither-dissolves the outside (transition), then hard-culls at 1.
          if (ejCullEnabled > 0.5) {
            float ejCd = dot(vEjWorldPos - ejCullOrigin, ejCullNormal); // + = toward camera
            if (ejCd > ejCullFront || ejCd < -ejCullBack) {
              if (ejCullFade >= 0.999) {
                discard;
              } else {
                float ejDith = fract(sin(dot(gl_FragCoord.xy, vec2(12.9898, 78.233))) * 43758.5453);
                if (ejDith < ejCullFade) discard;
              }
            }
          }
          if (ejMode > 0.5) {
            vec3 ejCol = gl_FragColor.rgb;
            // Radiating ring from the tap point (world distance).
            float ejD = length(vEjWorldPos - ejPulseOrigin);
            float ejRing = 1.0 - smoothstep(0.0, ejPulseWidth, abs(ejD - ejPulseRadius));
            ejCol += ejPulseTint * ejRing * ejStrength;
            // Region reveal wave (depth-keyed); mode 3 = solid hold.
            if (ejMode > 1.5) {
              float ejFront = ejWaveProgress * ejWaveMaxDepth;
              float ejIn = step(0.0, vEjDepth);
              float ejFilled = (ejMode > 2.5)
                ? ejIn
                : ejIn * (1.0 - smoothstep(ejFront - 1.5, ejFront + 1.5, vEjDepth));
              ejCol = mix(ejCol, mix(ejCol, ejTint, 0.38), ejFilled * ejStrength);
              float ejEdge = ejIn * exp(-abs(vEjDepth - ejFront) * 0.6);
              ejCol += ejTint * ejEdge * ejStrength * 0.5;
            }
            gl_FragColor = vec4(ejCol, gl_FragColor.a);
          }
          #endif
        `,
      };
    }
    return null;
  }
}
